import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PatientService } from '../../../services/patient/patient.service';

export interface ResponsavelLegal {
  nmContato: string;
  nrCpf: string;
  nrDdd: string;
  nrTelefone: string;
  dsEmail: string;
  dsEndereco: string;
  nrEndereco: string;
  dsComplemento: string;
  dsBairro: string;
  dsMunicipio: string;
  sgEstado: string;
  cdCep: string;
  cdMunicipioIbge: number | null;
}

@Component({
  selector: 'app-responsavel-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  // Sem modo escuro proprio: fica no tema claro mesmo com o app escuro.
  host: { class: 'tema-claro', style: 'display: contents' },
  templateUrl: './responsavel-modal.component.html',
})
export class ResponsavelModalComponent implements OnChanges {
  @Input() cdPessoaFisica: number | null = null;
  // Trava "o responsavel nao pode ser o proprio paciente".
  @Input() cpfPaciente: string | null = null;
  // Selo "Obrigatorio" no cabecalho: so o Step 1 usa, para menor de idade.
  @Input() obrigatorio = false;
  @Input() aberto = false;

  @Output() fechar = new EventEmitter<void>();
  @Output() salvo = new EventEmitter<ResponsavelLegal>();
  @Output() carregado = new EventEmitter<{ responsavel: ResponsavelLegal; completo: boolean }>();

  form: ResponsavelLegal = ResponsavelModalComponent.formVazio();
  erros: string[] = [];
  isSalvando = false;
  isBuscandoCep = false;

  buscaCpf = '';
  isBuscando = false;
  buscaMsg = '';
  buscaErro = false;

  constructor(private patientService: PatientService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['cdPessoaFisica'] && this.cdPessoaFisica) this.carregar();
    if (changes['aberto']?.currentValue === true) {
      this.erros = [];
      this.buscaCpf = '';
      this.buscaMsg = '';
      this.buscaErro = false;
    }
  }

  private static formVazio(): ResponsavelLegal {
    return {
      nmContato: '', nrCpf: '', nrDdd: '', nrTelefone: '', dsEmail: '',
      dsEndereco: '', nrEndereco: '', dsComplemento: '', dsBairro: '',
      dsMunicipio: '', sgEstado: '', cdCep: '', cdMunicipioIbge: null,
    };
  }

  private async carregar(): Promise<void> {
    this.form = ResponsavelModalComponent.formVazio();
    try {
      const data = await this.patientService.getResponsavelLegal(Number(this.cdPessoaFisica));
      if (data?.temResponsavel && data.responsavel) {
        const r = data.responsavel;
        this.form = {
          nmContato:       r.nmContato      ?? '',
          nrCpf:           r.nrCpf          ?? '',
          nrDdd:           r.nrDdd          ?? '',
          nrTelefone:      r.nrTelefone     ?? '',
          dsEmail:         r.dsEmail        ?? '',
          dsEndereco:      r.dsEndereco     ?? '',
          nrEndereco:      r.nrEndereco     ?? '',
          dsComplemento:   r.dsComplemento  ?? '',
          dsBairro:        r.dsBairro       ?? '',
          dsMunicipio:     r.dsMunicipio    ?? '',
          sgEstado:        r.sgEstado       ?? '',
          cdCep:           r.cdCep          ?? '',
          cdMunicipioIbge: r.cdMunicipioIbge ?? null,
        };
        this.carregado.emit({ responsavel: this.form, completo: data.responsavelCompleto ?? true });
      }
    } catch { /* silencioso - se falhar so nao preenche */ }
  }

  onBuscaCpfInput(): void {
    this.buscaCpf = this.buscaCpf.replace(/\D/g, '').slice(0, 11);
    this.buscaMsg = '';
    this.buscaErro = false;
  }

  async buscarPorCpf(): Promise<void> {
    if (this.isBuscando) return;
    const cpf = (this.buscaCpf || '').replace(/\D/g, '');
    if (cpf.length !== 11) {
      this.buscaMsg = 'Informe um CPF válido (11 dígitos).';
      this.buscaErro = true;
      return;
    }
    if (cpf === this.cpfLimpoDoPaciente()) {
      this.buscaMsg = 'O responsável não pode ser o próprio paciente.';
      this.buscaErro = true;
      return;
    }
    this.isBuscando = true;
    this.buscaMsg = '';
    this.buscaErro = false;
    try {
      let resp: any = null;
      try { resp = await this.patientService.getPersonByDocument(cpf); } catch {}
      if (!resp || !resp.id) resp = await this.patientService.getPatientByCPF(cpf);
      if (!resp || !resp.id) {
        this.buscaMsg = 'Nenhum paciente encontrado com esse CPF.';
        this.buscaErro = true;
        return;
      }
      const phone = (resp.phone ?? '').replace(/\D/g, '');
      const addr = resp.address ?? {};
      this.form = {
        nmContato:       resp.name ?? '',
        nrCpf:           cpf,
        nrDdd:           phone.slice(0, 2),
        nrTelefone:      phone.slice(2),
        dsEmail:         resp.email ?? '',
        dsEndereco:      addr.street ?? '',
        nrEndereco:      addr.number != null ? String(addr.number) : '',
        dsComplemento:   addr.complement ?? '',
        dsBairro:        addr.neighborhood ?? '',
        dsMunicipio:     addr.city ?? '',
        sgEstado:        addr.state ?? '',
        cdCep:           (addr.zipcode ?? '').replace(/\D/g, ''),
        cdMunicipioIbge: null,
      };
      this.buscaMsg = 'Dados pré-preenchidos. Confira e salve.';
      this.buscaErro = false;
    } catch {
      this.buscaMsg = 'Erro ao buscar. Tente novamente.';
      this.buscaErro = true;
    } finally {
      this.isBuscando = false;
    }
  }

  onCpfInput(event: Event): void {
    this.form.nrCpf = (event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 11);
  }

  onCepInput(event: Event): void {
    this.form.cdCep = (event.target as HTMLInputElement).value.replace(/\D/g, '').slice(0, 8);
  }

  // COMPL_PESSOA_FISICA.NR_ENDERECO é NUMBER(5): "S/N", "15A" ou 6+ dígitos
  // fazem o Oracle recusar a gravação.
  onNumeroInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.form.nrEndereco = input.value.replace(/\D/g, '').slice(0, 5);
    input.value = this.form.nrEndereco;
  }

  formatarCpf(cpf: string): string {
    const d = (cpf || '').replace(/\D/g, '').slice(0, 11);
    if (d.length !== 11) return d;
    return d.slice(0, 3) + '.' + d.slice(3, 6) + '.' + d.slice(6, 9) + '-' + d.slice(9);
  }

  formatarCep(cep: string): string {
    const d = (cep || '').replace(/\D/g, '').slice(0, 8);
    if (d.length <= 5) return d;
    return d.slice(0, 5) + '-' + d.slice(5);
  }

  async buscarCep(): Promise<void> {
    if (this.isBuscandoCep) return;
    const cep = (this.form.cdCep || '').replace(/\D/g, '');
    if (cep.length !== 8) return;
    this.isBuscandoCep = true;
    try {
      const res = await fetch('https://viacep.com.br/ws/' + cep + '/json/');
      const data = await res.json();
      if (!data.erro) {
        this.form.dsEndereco  = data.logradouro || this.form.dsEndereco;
        this.form.dsBairro    = data.bairro     || this.form.dsBairro;
        this.form.dsMunicipio = data.localidade || this.form.dsMunicipio;
        this.form.sgEstado    = data.uf         || this.form.sgEstado;
        // COMPL_PESSOA_FISICA.CD_MUNICIPIO_IBGE é VARCHAR2(6) e guarda o código do
        // SUS_MUNICIPIO (IBGE sem o dígito verificador). O ViaCEP devolve 7 dígitos
        // (ex.: São Luís 2111300) e enviá-lo assim faz o Oracle recusar a gravação.
        const ibge6 = String(data.ibge ?? '').replace(/\D/g, '').slice(0, 6);
        if (ibge6.length === 6) this.form.cdMunicipioIbge = Number(ibge6);
      }
    } catch {} finally {
      this.isBuscandoCep = false;
    }
  }

  private cpfLimpoDoPaciente(): string {
    return (this.cpfPaciente ?? '').replace(/\D/g, '');
  }

  private validar(): string[] {
    const erros: string[] = [];
    const cpfLimpo = (this.form.nrCpf || '').replace(/\D/g, '');
    const cepLimpo = (this.form.cdCep || '').replace(/\D/g, '');
    if (!this.form.nmContato?.trim())  erros.push('Nome do responsável é obrigatório');
    if (cpfLimpo.length !== 11)        erros.push('CPF inválido');
    if (!this.form.nrTelefone?.trim()) erros.push('Telefone é obrigatório');
    if (cepLimpo.length !== 8)         erros.push('CEP inválido');
    if (!this.form.dsEndereco?.trim()) erros.push('Logradouro é obrigatório');
    if (!String(this.form.nrEndereco ?? '').replace(/\D/g, '')) erros.push('Número é obrigatório (somente dígitos)');
    if (!this.form.dsBairro?.trim())   erros.push('Bairro é obrigatório');
    if (!this.form.dsMunicipio?.trim()) erros.push('Cidade é obrigatória');
    if (!this.form.sgEstado || this.form.sgEstado.length !== 2) erros.push('UF é obrigatória');
    if (cpfLimpo && cpfLimpo === this.cpfLimpoDoPaciente())
      erros.push('O responsável não pode ser o próprio paciente');
    return erros;
  }

  async salvar(): Promise<void> {
    if (this.isSalvando) return;

    this.erros = this.validar();
    if (this.erros.length > 0) return;
    if (!this.cdPessoaFisica) {
      this.erros = ['Nenhum paciente selecionado.'];
      return;
    }

    this.isSalvando = true;
    try {
      const payload = {
        ...this.form,
        nrCpf: (this.form.nrCpf || '').replace(/\D/g, ''),
        cdCep: (this.form.cdCep || '').replace(/\D/g, ''),
        sgEstado: (this.form.sgEstado || '').toUpperCase(),
        nrEndereco: String(this.form.nrEndereco ?? '').replace(/\D/g, '').slice(0, 5),
        dsComplemento: (this.form.dsComplemento || '').slice(0, 40),
      };
      await this.patientService.salvarResponsavelLegal(Number(this.cdPessoaFisica), payload);
      this.form = { ...this.form, ...payload };
      this.salvo.emit(this.form);
      this.fechar.emit();
    } catch (e) {
      // Mostra o motivo devolvido pelo back — o texto genérico escondia a causa real.
      const motivo = typeof e === 'string' ? e : (e as any)?.message;
      this.erros = [motivo || 'Erro ao salvar. Verifique os dados e tente novamente.'];
    } finally {
      this.isSalvando = false;
    }
  }
}
